import { useState } from "react";
import tokenService from "utils/TokenService";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

export default async function fetchExport(
  exportName: string,
  variables: Record<string, unknown> = {},
) {
  const jwt = await tokenService.getOrRefreshToken();

  const origin =
    window.location.hostname === "localhost" ? "http://localhost:8080" : "";

  const url = `${origin}/export/${exportName}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify(variables),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch export: ${response.statusText}`);
  }

  const blob = await response.blob();
  const fileUrl = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = fileUrl;

  const contentDisposition = response.headers.get("content-disposition");
  const fileNameMatch = contentDisposition?.match(/filename="(.+)"/);
  const fileName = fileNameMatch ? fileNameMatch[1] : `${exportName}.xlsx`;
  link.download = fileName;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return fileName;
}

export function useFetchExport(): [
  fetchExport: (
    ...params: Parameters<typeof fetchExport>
  ) => Promise<string | undefined>,
  { isLoading: boolean },
] {
  const [isLoading, setIsLoading] = useState(false);

  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  async function handleFetchExport(
    exportName: string,
    variables: Record<string, unknown> = {},
  ) {
    setIsLoading(true);
    try {
      const fileName = await fetchExport(exportName, variables);
      showSuccessToast("Export successfully downloaded", {
        description: fileName,
      });
      return fileName;
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  }

  return [handleFetchExport, { isLoading }];
}
